import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Calendar, CheckSquare, StickyNote, DollarSign, Music } from 'lucide-react';
import { Sidebar, TopBar, BottomBar } from '@/components/shell/Shell';
import { CinematicBackground } from '@/components/CinematicBackground';
import { usePlayer } from '@/context/PlayerContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';

const FAB_ACTIONS = [
  { icon: CheckSquare, label: 'Tarea', to: '/plannerapp/tasks', color: '#3EB489' },
  { icon: Calendar, label: 'Evento', to: '/plannerapp/calendar', color: '#C9A84C' },
  { icon: StickyNote, label: 'Nota', to: '/plannerapp/notes', color: '#A8E6CE' },
  { icon: DollarSign, label: 'Gasto', to: '/plannerapp/finance', color: '#2D4A3E' },
];

const MiniPlayer = () => {
  const { video, visible, close } = usePlayer();
  if (!visible || !video) return null;
  return (
    <motion.div drag dragMomentum={false} initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-20 md:bottom-6 right-4 z-50 w-72 pp-glass-strong rounded-2xl overflow-hidden shadow-2xl" data-testid="mini-player">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <span className="text-xs font-medium truncate flex items-center gap-1"><Music size={13} className="pp-mint" /> {video.title}</span>
        <button onClick={close} data-testid="mini-player-close"><X size={15} /></button>
      </div>
      <iframe title="yt" width="288" height="162" src={`https://www.youtube.com/embed/${video.id}?autoplay=1`} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen />
    </motion.div>
  );
};

// Pie de página de las vistas de la app — marca 7-study (creador) + PlannerPRO.
const AppFooter = () => (
  <footer data-testid="app-footer" className="md:pl-0 border-t border-[rgba(150,170,160,0.14)] mt-6">
    <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-[1500px] mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 text-center">
      <img src="/brand/logo-principal.jpg" alt="PlannerPRO" className="w-6 h-6 rounded-md object-cover shrink-0" />
      <span className="text-[11px] sm:text-xs text-muted-foreground">
        © 2026 <span className="font-semibold">Planner<span className="pp-gold">PRO</span></span> · <a href="https://www.plannerpro.win" target="_blank" rel="noreferrer" className="pp-mint hover:underline">www.plannerpro.win</a>
        <span className="mx-1.5 opacity-40">·</span>
        Desarrollado por <a href="https://www.7-study.com" target="_blank" rel="noreferrer" className="pp-mint font-semibold hover:underline">7-study</a> (<a href="https://www.7-study.com" target="_blank" rel="noreferrer" className="hover:underline">www.7-study.com</a>)
        <span className="mx-1.5 opacity-40">·</span>
        Derechos reservados
      </span>
    </div>
  </footer>
);

export const AppLayout = () => {
  const [fabOpen, setFabOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  // Cierre de sesión automático tras 10 minutos de inactividad.
  useEffect(() => {
    const IDLE_MS = 10 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        toast.info('Sesión cerrada por inactividad (10 min). Vuelve a iniciar sesión.');
        logout();
      }, IDLE_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); events.forEach((e) => window.removeEventListener(e, reset)); };
  }, [logout]);

  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setCmdOpen(true); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const nav = [
    { label: 'Dashboard', to: '/plannerapp' }, { label: 'Calendario', to: '/plannerapp/calendar' },
    { label: 'Tareas & Proyectos', to: '/plannerapp/tasks' }, { label: 'Finanzas', to: '/plannerapp/finance' },
    { label: 'Notas IA', to: '/plannerapp/notes' }, { label: 'Correos', to: '/plannerapp/emails' }, { label: 'Notificaciones', to: '/plannerapp/notifications' },
    { label: 'Ajustes', to: '/plannerapp/settings' },
  ];

  return (
    <div className="App relative min-h-screen">
      <CinematicBackground />
      <Sidebar />
      <TopBar onSearch={() => setCmdOpen(true)} />
      <main className="md:pl-[248px] pt-16">
        <div className="px-4 sm:px-6 lg:px-8 py-6 pb-28 md:pb-10 max-w-[1500px] mx-auto">
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}>
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
        <AppFooter />
      </main>
      <BottomBar />
      <MiniPlayer />

      {/* FAB (mobile only) */}
      <div className="md:hidden fixed bottom-20 left-4 z-40">
        <AnimatePresence>
          {fabOpen && FAB_ACTIONS.map((a, i) => (
            <motion.button key={a.label} data-testid={`fab-${a.label.toLowerCase()}`}
              initial={{ scale: 0, y: 0, opacity: 0 }} animate={{ scale: 1, y: -(i + 1) * 56, opacity: 1 }} exit={{ scale: 0, y: 0, opacity: 0 }}
              transition={{ delay: i * 0.04 }}
              onClick={() => { navigate(a.to); setFabOpen(false); }}
              className="absolute bottom-0 w-12 h-12 rounded-full grid place-items-center pp-glass-strong shadow-lg" style={{ color: a.color }}>
              <a.icon size={20} />
            </motion.button>
          ))}
        </AnimatePresence>
        <motion.button data-testid="fab-main" onClick={() => setFabOpen((o) => !o)} whileTap={{ scale: 0.92 }}
          className="w-14 h-14 rounded-full grid place-items-center pp-glow-mint text-white shadow-xl" style={{ background: '#3EB489' }}>
          <motion.span animate={{ rotate: fabOpen ? 45 : 0 }}><Plus size={26} /></motion.span>
        </motion.button>
      </div>

      <CommandDialog open={cmdOpen} onOpenChange={setCmdOpen}>
        <CommandInput placeholder="Navegar a..." />
        <CommandList>
          <CommandEmpty>Sin resultados.</CommandEmpty>
          <CommandGroup heading="Módulos">
            {nav.map((n) => (
              <CommandItem key={n.to} onSelect={() => { navigate(n.to); setCmdOpen(false); }}>{n.label}</CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </div>
  );
};
